import BaseApiService from './BaseApiService';

class SubjectAssignmentService extends BaseApiService {
  
  // ==================== TEACHER-SUBJECT ASSIGNMENTS ====================
  
  /**
   * Assign a teacher to a subject
   */
  async assignTeacherToSubject(assignmentData) {
    return this.makeRequest('/api/assignments/teacher-subject', {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    });
  }

  /**
   * Get all subjects assigned to a teacher
   */
  async getTeacherSubjects(teacherId) {
    return this.makeRequest(`/api/assignments/teacher/${teacherId}/subjects`);
  }

  /**
   * Get all teachers assigned to a subject
   */
  async getSubjectTeachers(subjectId) {
    return this.makeRequest(`/api/assignments/subject/${subjectId}/teachers`);
  }

  // ==================== STUDENT-SUBJECT ENROLLMENTS ====================

  /**
   * Enroll a student in a subject
   */
  async enrollStudentInSubject(enrollmentData) {
    return this.makeRequest('/api/assignments/bulk/student-subjects', {
      method: 'POST',
      body: JSON.stringify(enrollmentData)
    });
  }

  /**
   * Get all subjects enrolled by a student
   */
  async getStudentSubjects(studentId) {
    return this.makeRequest(`/api/assignments/student/${studentId}/subjects`);
  }

  /**
   * Get all students enrolled in a subject
   */
  async getSubjectStudents(subjectId) {
    return this.makeRequest(`/api/assignments/subject/${subjectId}/students`);
  }

  /**
   * Get all students with their enrolled subjects (College & School)
   * Uses unified backend endpoint: /api/assignments/students-with-subjects
   */
  async getAllStudentSubjectEnrollments() {
    try {
      console.log('🚀 Fetching all students with their subjects from /api/assignments/students-with-subjects ...');

      const response = await this.makeRequest('/api/assignments/students-with-subjects', {
        method: 'GET'
      });

      console.log('📥 students-with-subjects API response:', response);

      let students = [];

      // Expected shape: { success, students: [...] }
      if (response && Array.isArray(response.students)) {
        students = response.students;
      } else if (Array.isArray(response)) {
        // Fallback: raw array
        students = response;
      } else if (response && Array.isArray(response.data)) {
        // Fallback: { data: [...] }
        students = response.data;
      } else {
        console.warn('⚠️ Unexpected students-with-subjects response format:', response);
        return { success: false, students: [] };
      }

      console.log(`✅ Loaded ${students.length} students with their subject enrollments`);

      return {
        success: true,
        students
      };
    } catch (error) {
      console.error('❌ Error fetching all students with subjects from unified endpoint:', error);
      throw error;
    }
  }

  /**
   * Get class-subject assignments (groups students by class)
   */
  async getClassSubjectAssignments() {
    try {
      const startTime = performance.now();
      console.log('Fetching class-subject assignments from API...');
      
      const response = await this.makeRequest('/api/assignments/students-with-subjects');
      
      const fetchTime = performance.now() - startTime;
      console.log(`API fetch completed in ${fetchTime.toFixed(2)}ms`);
      
      if (response && response.success && response.students) {
        const processingStart = performance.now();
        
        // Optimized grouping using single pass with Set for subjects
        const classSubjectsMap = new Map();
        
        response.students.forEach(student => {
          const className = student.className || student.courseName || student.class || student.classLevel || 'No Class Assigned';
          const section = student.section || student.sectionName || null;
          
          if (!classSubjectsMap.has(className)) {
            classSubjectsMap.set(className, {
              className: className,
              section: section,
              studentCount: 0,
              subjectsMap: new Map(),
              academicYear: student.academicYear
            });
          }
          
          const classData = classSubjectsMap.get(className);
          // Ensure section is preserved if some students carry it
          if (!classData.section && section) {
            classData.section = section;
          }
          classData.studentCount++;
          
          // Add subjects efficiently
          if (student.subjects && Array.isArray(student.subjects)) {
            student.subjects.forEach(subject => {
              if (subject.subjectId && !classData.subjectsMap.has(subject.subjectId)) {
                classData.subjectsMap.set(subject.subjectId, {
                  subjectId: subject.subjectId,
                  subjectName: subject.subjectName || subject.name,
                  subjectCode: subject.subjectCode || subject.code
                });
              }
            });
          }
        });
        
        // Convert to array format
        const classSubjectsArray = Array.from(classSubjectsMap.values()).map(classData => ({
          className: classData.className,
          section: classData.section,
          studentCount: classData.studentCount,
          subjects: Array.from(classData.subjectsMap.values()),
          academicYear: classData.academicYear
        }));
        
        const processingTime = performance.now() - processingStart;
        console.log(`Data processing completed in ${processingTime.toFixed(2)}ms`);
        console.log(`Total classes: ${classSubjectsArray.length}`);
        
        return {
          success: true,
          classes: classSubjectsArray
        };
      }
      
      return {
        success: false,
        classes: []
      };
    } catch (error) {
      console.error('Error getting class subject assignments:', error);
      throw error;
    }
  }

  // ==================== BULK OPERATIONS ====================

  /**
   * Bulk enroll students in multiple subjects
   */
  async bulkEnrollStudents(bulkData) {
    return this.makeRequest('/api/assignments/bulk/student-subjects', {
      method: 'POST',
      body: JSON.stringify(bulkData)
    });
  }

  /**
   * Assign teacher to all subjects in a department
   */
  async assignTeacherToDepartment(assignmentData) {
    return this.makeRequest('/api/assignments/teacher-department', {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    });
  }

  /**
   * Bulk assign teacher to multiple subjects
   */
  async bulkAssignTeacher(bulkData) {
    return this.makeRequest('/api/assignments/bulk/teacher-subjects', {
      method: 'POST',
      body: JSON.stringify(bulkData)
    });
  }

  // ==================== DELETE OPERATIONS ====================

  /**
   * Delete subject from class (remove all students' enrollment in that subject for the class)
   * Backend expects payload: { subjectId, className, section }
   */
  async deleteSubjectFromClass(className, section, subjectId) {
    return this.makeRequest(`/api/assignments/student-subject/class`, {
      method: 'DELETE',
      body: JSON.stringify({ subjectId, className, section })
    });
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Check if teacher is authorized for a subject
   */
  async checkTeacherAuthorization(teacherId, subjectId) {
    return this.makeRequest(`/api/assignments/check/teacher/${teacherId}/subject/${subjectId}`);
  }
}

// ✅ Export single instance
const subjectAssignmentService = new SubjectAssignmentService();
export default subjectAssignmentService;
